"use client";

import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { usePathname } from "next/navigation";
import { Send, Sparkles } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import { useSkipAnimations } from "@/lib/animations";
import {
  MAX_MESSAGE_LENGTH,
  useConversation,
  useHydrated,
} from "@/lib/chat/conversation";
import { openingSuggestions } from "@/lib/chat/suggestions";
import { useTypedPlaceholder } from "./useTypedPlaceholder";

interface Props {
  /** Narrow (docked / mobile) rendering. */
  compact: boolean;
  placeholderKey: "chat.askbar.placeholder" | "chat.input.placeholder";
  /**
   * Type example questions into the placeholder while the field is untouched.
   * Only the resting Ask-Bar asks for this — see AssistantPanel.
   */
  invite?: boolean;
}

/**
 * The input. Always the bottom row of the panel, in every appearance — that is
 * what makes the Ask-Bar and the docked panel the same component.
 *
 * Board 14 "V4 · Gewählt" draws it as a real field rather than a bare row: an
 * inset surface with its own border and radius, an icon chip and the send
 * button inside it. That is what makes the resting Ask-Bar read as askable
 * instead of as a decorative strip. The outer row keeps its 56px so the
 * reserved ASK_BAR_HEIGHT of 99 is unchanged: 8 + 40 + 8.
 */
const Composer = forwardRef<HTMLInputElement, Props>(function Composer(
  { compact, placeholderKey, invite = false },
  ref
) {
  const { t, locale } = useTranslation();
  const pathname = usePathname();
  const skipAnimations = useSkipAnimations();
  const { send, isStreaming, open } = useConversation();
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);

  /*
   * The panel is server-rendered, so the field is on screen and typeable before
   * React has attached anything to it. Someone who starts typing straight away
   * would otherwise have their text thrown away by the first controlled render.
   * Adopt whatever the DOM already holds instead.
   *
   * Enter pressed in that window still does nothing — there is no handler yet —
   * but the question survives, so pressing it again sends it.
   */
  const hydrated = useHydrated();
  const localRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const el = localRef.current;
    if (el && el.value && !value) setValue(el.value);
    // Once only, as hydration completes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated]);

  /*
   * The invitation, and the two things that end it: the visitor is in the
   * field, or has already written something. Both mean the Ask-Bar has done its
   * job and typing over them would be noise.
   */
  const typed = useTypedPlaceholder(
    openingSuggestions(pathname, locale),
    invite && hydrated && !skipAnimations && !focused && value.length === 0,
  );

  const canSend = value.trim().length > 0 && !isStreaming;

  const submit = () => {
    if (!canSend) return;
    send(value);
    setValue("");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      className="flex shrink-0 items-center bg-assistant-surface p-2"
      style={compact ? undefined : { height: 56 }}
    >
      {/*
        focus-within is the escalation board 13 §02 describes: the resting field
        already carries the mint edge, so focus raises it to full mint plus a
        ring rather than introducing the accent for the first time.
      */}
      <div
        className={`flex min-w-0 flex-1 items-center rounded-md border border-assistant-field-border bg-assistant-field transition-shadow focus-within:border-mint-text focus-within:shadow-[0_0_0_3px_var(--assistant-ring)] ${
          compact ? "h-8 gap-2 pr-1 pl-1" : "h-10 gap-2.5 pr-1 pl-1.5"
        }`}
      >
        <span
          className={`flex shrink-0 items-center justify-center rounded-md bg-mint-faint ${
            compact ? "h-6 w-6" : "h-7 w-7"
          }`}
          aria-hidden="true"
        >
          {/*
            It idles only while the bar is resting and untouched — that is the
            invitation. It deliberately does NOT report streaming: the answer's
            own avatar already turns over while it is being written, and a
            second spinner in the field reads as the input being busy, which it
            is not. Once the panel is open this mark holds still.
          */}
          <Sparkles
            size={compact ? 14 : 16}
            className={`text-mint-text ${invite ? "assistant-spark" : ""}`}
          />
        </span>
        <input
          ref={(node) => {
            localRef.current = node;
            if (typeof ref === "function") ref(node);
            else if (ref) ref.current = node;
          }}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => {
            setFocused(true);
            open();
          }}
          onBlur={() => setFocused(false)}
          maxLength={MAX_MESSAGE_LENGTH}
          placeholder={typed ?? t(placeholderKey)}
          aria-label={t("chat.askbar.label")}
          data-assistant-composer
          // Interactive only once React has taken the field over.
          data-assistant-ready={hydrated ? "true" : undefined}
          className={`min-w-0 flex-1 bg-transparent ${
            compact ? "text-xs" : "text-sm"
          } text-text-primary outline-none placeholder:text-text-muted`}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!canSend}
          aria-label={t("chat.send")}
          className={`flex shrink-0 items-center justify-center rounded-sm bg-mint text-black transition-colors hover:bg-mint-hover disabled:cursor-not-allowed disabled:opacity-35 ${
            compact ? "h-6 w-6" : "h-8 w-8"
          }`}
        >
          <Send size={compact ? 12 : 15} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
});

export default Composer;
